import React, { useState, useEffect, useRef, useCallback } from 'react';
import PageHeader from '../components/common/PageHeader';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Autocomplete,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Skeleton,
  FormHelperText,
} from '@mui/material';
import {
  Add,
  Delete,
  Close,
  Send,
  Schedule,
  Email,
  Notifications,
  Business,
  CheckBoxOutlineBlank,
  CheckBox,
  Visibility,
  People,
} from '@mui/icons-material';
import { CarbonCareDatePicker } from '../components/shared/CarbonCareDatePicker';
import { adminUpdatesApi, AdminUpdate, CreateAdminUpdateData } from '../services/api/adminUpdatesApi';
import { companyApi } from '../services/api/companyApi';
import { Company } from '../types/company';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

interface DeliveryToggleCardProps {
  active: boolean;
  Icon: React.ElementType;
  label: string;
  onToggle: (next: boolean) => void;
}

/** A single delivery/scheduling toggle card; extracted to keep the page component below the cognitive-complexity threshold (its sx ternaries live here instead). */
const DeliveryToggleCard: React.FC<DeliveryToggleCardProps> = ({ active, Icon, label, onToggle }) => (
  <Box
    sx={{
      p: 1.5,
      border: '1px solid',
      borderColor: active ? 'primary.main' : 'divider',
      borderRadius: 1,
      cursor: 'pointer',
      bgcolor: active ? 'primary.50' : 'transparent',
      transition: 'all 0.2s',
    }}
    onClick={() => onToggle(!active)}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
      <Icon fontSize="small" sx={{ color: active ? 'primary.main' : 'text.secondary' }} />
      <Switch size="small" checked={active} onChange={(e) => onToggle(e.target.checked)} />
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 500 }}>{label}</Typography>
  </Box>
);

/** Start icon for the create/schedule announcement button (avoids a nested ternary in JSX). */
const renderAnnouncementStartIcon = (submitting: boolean, scheduleEnabled: boolean): React.ReactNode => {
  if (submitting) return <CircularProgress size={20} />;
  if (scheduleEnabled) return <Schedule />;
  return <Send />;
};

export interface AnnouncementFormState {
  title: string;
  body: string;
  sendViaEmail: boolean;
  sendViaNotification: boolean;
  scheduleEnabled: boolean;
  scheduledDateTime: string;
  targetAllUsers: boolean;
  selectedUserTypeCount: number;
  targetAllCompanies: boolean;
  selectedCompanyCount: number;
  customEmailsInput: string;
}

/** Validate the announcement form; returns an error message or null. Extracted to keep the page component below the cognitive-complexity threshold. */
export const getAnnouncementFormError = (s: AnnouncementFormState): string | null => {
  if (!s.title.trim()) return 'Title is required';
  if (!s.body.trim()) return 'Body/Message is required';
  if (!s.sendViaEmail && !s.sendViaNotification) {
    return 'Select at least one delivery method (Email or Notification)';
  }
  if (s.scheduleEnabled && !s.scheduledDateTime) return 'Please select a scheduled time';

  const hasPlatformRecipients =
    s.targetAllUsers || s.selectedUserTypeCount > 0 || s.targetAllCompanies || s.selectedCompanyCount > 0;
  const hasCustomRecipients = s.customEmailsInput.trim().length > 0;
  if (!hasPlatformRecipients && !hasCustomRecipients) {
    return 'Please select at least one recipient (User Type, Licensee, or External Email)';
  }

  const isUserTargeted = s.targetAllUsers || s.selectedUserTypeCount > 0;
  const isLicenseeTargeted = s.targetAllCompanies || s.selectedCompanyCount > 0;
  if (isUserTargeted && !isLicenseeTargeted) {
    return 'Please select target licensees for the selected user types';
  }
  // Both halves are required: a licensee with no user type resolves to nobody.
  if (isLicenseeTargeted && !isUserTargeted) {
    return 'Please select at least one user type (or All) for the selected licensees';
  }

  return null;
};

/** Verb prefix for the submit button label (avoids a nested IIFE/ternary in JSX). */
export const getAnnouncementActionVerb = (submitting: boolean, scheduleEnabled: boolean): string => {
  if (submitting) return 'Creating...';
  if (scheduleEnabled) return 'Schedule';
  return 'Send';
};

/** Normalize raw target_user_types into a numeric array (or null for "all"). */
export const normalizeTargetUserTypes = (raw: number | number[] | null): number[] | null => {
  if (typeof raw === 'number') return [raw];
  if (raw === null) return null;
  if (!Array.isArray(raw)) return [];
  return raw;
};

/** Loading skeleton rows for the updates table. */
const renderUpdatesSkeletonRows = (): React.ReactNode =>
  Array.from({ length: 8 }, (_, i) => `update-skeleton-${i}`).map((rowKey, idx) => (
    <TableRow key={rowKey}>
      <TableCell><Skeleton variant="text" width={`${50 + ((idx * 9) % 30)}%`} /></TableCell>
      <TableCell sx={{ maxWidth: 300 }}><Skeleton variant="text" width="90%" /></TableCell>
      <TableCell><Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="circular" width={18} height={18} />
          <Skeleton variant="circular" width={18} height={18} />
        </Box>
      </TableCell>
      <TableCell><Skeleton variant="text" width={130} /></TableCell>
      <TableCell><Skeleton variant="text" width={130} /></TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Box>
      </TableCell>
    </TableRow>
  ));

/** External-email recipient chips for the view-detail dialog (returns null when none); extracted to keep the page component below the cognitive-complexity threshold. */
const renderCustomEmailRecipients = (customEmails: string[] | null | undefined): React.ReactNode => {
  if (!customEmails || customEmails.length === 0) return null;
  return (
    <>
      <Divider />
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email fontSize="small" />
          External Email Recipients
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {customEmails.map((email) => (
            <Chip
              key={email}
              label={email}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      </Box>
    </>
  );
};

interface UpdatesPaginationProps {
  loading: boolean;
  count: number;
  rowsPerPage: number;
  page: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/** Pagination control for the updates table (hidden while loading); extracted to keep the page component below the cognitive-complexity threshold. */
const renderUpdatesPagination = ({
  loading,
  count,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
}: UpdatesPaginationProps): React.ReactNode => {
  if (loading) return null;
  return (
    <TablePagination
      rowsPerPageOptions={[10]}
      component="div"
      count={count}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  );
};

interface TargetLicenseesProps {
  targetCompanyIds: number[] | null;
  loading: boolean;
  companies: Company[];
}

/** Target-licensees chips for the view-detail dialog (extracted to keep the page component below the cognitive-complexity threshold). */
const renderTargetLicensees = ({ targetCompanyIds, loading, companies }: TargetLicenseesProps): React.ReactNode => {
  if (targetCompanyIds === null) {
    return <Chip label="All Licensees" color="info" size="small" />;
  }
  if (targetCompanyIds.length === 0) {
    return <Chip label="None" size="small" />;
  }
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {companies.length > 0
        ? companies.map((company) => (
            <Chip
              key={company.id}
              label={company.name}
              size="small"
              variant="outlined"
              icon={<Business sx={{ fontSize: '14px !important' }} />}
            />
          ))
        : targetCompanyIds.map((id) => (
            <Chip
              key={id}
              label={`Licensee #${id}`}
              size="small"
              variant="outlined"
              icon={<Business sx={{ fontSize: '14px !important' }} />}
            />
          ))}
    </Box>
  );
};

interface UpdatesTableBodyProps {
  loading: boolean;
  updates: AdminUpdate[];
  getStatusChip: (status: string) => React.ReactElement;
  onViewDetail: (update: AdminUpdate) => void;
  onDeleteClick: (id: string) => void;
}

/** Table body for the updates list: skeleton, empty, or data rows (extracted to keep the page component below the cognitive-complexity threshold). */
const renderUpdatesTableBody = ({
  loading,
  updates,
  getStatusChip,
  onViewDetail,
  onDeleteClick,
}: UpdatesTableBodyProps): React.ReactNode => {
  if (loading) {
    return renderUpdatesSkeletonRows();
  }
  if (updates.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No announcements yet. Create one to get started!
          </Typography>
        </TableCell>
      </TableRow>
    );
  }
  return updates.map((update) => {
    const canDelete = update.status === 'pending';
    return (
      <TableRow key={update.id} hover>
        <TableCell>{update.title}</TableCell>
        <TableCell sx={{ maxWidth: 300 }}>
          <Tooltip title={update.body}>
            <Typography variant="body2" noWrap>
              {update.body}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={update.error_message || ''}>
            {getStatusChip(update.status)}
          </Tooltip>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {update.send_via_email && (
              <Tooltip title="Email">
                <Email sx={{ fontSize: 18, color: '#1976d2' }} />
              </Tooltip>
            )}
            {update.send_via_notification && (
              <Tooltip title="Notification">
                <Notifications sx={{ fontSize: 18, color: '#ff9800' }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>{update.scheduled_at ? new Date(update.scheduled_at).toLocaleString() : '-'}</TableCell>
        <TableCell>{update.created_at ? new Date(update.created_at).toLocaleString() : '-'}</TableCell>
        <TableCell align="center">
          <Tooltip title="View Targets">
            <IconButton size="small" onClick={() => onViewDetail(update)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete' : 'Cannot delete sent/processing updates'}>
            <span>
              <IconButton
                size="small"
                onClick={() => onDeleteClick(update.id)}
                disabled={!canDelete}
                sx={{ color: canDelete ? 'error.main' : undefined }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  });
};

const AdminUpdatesPage: React.FC = () => {
  const { user } = useAuthStore();
  const [updates, setUpdates] = useState<AdminUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUpdates, setTotalUpdates] = useState(0);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [viewDetailOpen, setViewDetailOpen] = useState(false);
  const [viewDetailUpdate, setViewDetailUpdate] = useState<AdminUpdate | null>(null);
  const [viewDetailCompanies, setViewDetailCompanies] = useState<Company[]>([]);
  const [loadingViewDetail, setLoadingViewDetail] = useState(false);

  const [formData, setFormData] = useState<CreateAdminUpdateData>({
    title: '',
    body: '',
    send_via_email: true,
    send_via_notification: true,
    target_user_types: null,
    target_company_ids: null,
    scheduled_at: null,
  });

  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [targetAllUsers, setTargetAllUsers] = useState(false);
  const [selectedUserTypes, setSelectedUserTypes] = useState<number[]>([]);
  const [targetAllCompanies, setTargetAllCompanies] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);
  const [customEmailsInput, setCustomEmailsInput] = useState('');
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);

  // Load updates on mount and whenever sort changes
  useEffect(() => {
    loadUpdates(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDir]);

  const companySearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadCompanies = useCallback(async (query?: string) => {
    try {
      setLoadingCompanies(true);
      const response = await companyApi.getAll({
        limit: 10,
        fields: 'id,name',
        ...(query ? { query } : {}),
      });
      // Merge selected companies so they always remain visible
      const fetched = response.data;
      const merged = [...selectedCompanies.filter((sc) => !fetched.some((fc: Company) => fc.id === sc.id)), ...fetched];
      setCompanies(merged);
    } catch (err) {
      console.error('Failed to load companies:', err);
    } finally {
      setLoadingCompanies(false);
    }
  }, [selectedCompanies]);

  const loadUpdates = async (newPage = page, pageSize = rowsPerPage) => {
    try {
      setLoading(true);
      const res = await adminUpdatesApi.getAll({ page: newPage + 1, limit: pageSize, sortBy, sortOrder: sortDir });
      setUpdates(res.data);
      setTotalUpdates(res.total);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load updates:', err);
      setError(err.response?.data?.error || 'Failed to load updates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      title: '',
      body: '',
      send_via_email: true,
      send_via_notification: true,
      target_user_types: null,
      scheduled_at: null,
      custom_emails: null,
    });
    setScheduleEnabled(false);
    setScheduledDateTime('');
    setTargetAllUsers(false);
    setSelectedUserTypes([]);
    setTargetAllCompanies(false);
    setSelectedCompanies([]);
    setCustomEmailsInput('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handlePasteEmails = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    if (!pastedData) return;

    // Split by comma, semicolon, newline, or tab
    const emailArray = pastedData.split(/[,\n\r\t;]+/)
      .map(email => email.trim())
      .filter(email => {
        const emailRegex = /^[^\s@]{1,254}@[^\s@]{1,254}\.[^\s@]{1,254}$/;
        return emailRegex.test(email);
      });

    if (emailArray.length === 0) return;

    // Get current emails from input
    const currentEmails = customEmailsInput.split(',')
      .map(e => e.trim())
      .filter(e => e !== '');

    // Merge and filter unique
    const mergedEmails = [...currentEmails, ...emailArray].filter((email, index, self) =>
      self.indexOf(email) === index
    );
    setCustomEmailsInput(mergedEmails.join(', '));
  };

  const handleInputChange = (field: keyof CreateAdminUpdateData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToggleChange = (field: 'send_via_email' | 'send_via_notification', value: boolean) => {
    handleInputChange(field, value);
  };

  const getAccountTypeLabel = (type: AccountType): string => {
    const labels: Record<AccountType, string> = {
      [AccountType.SubCustomerAdmin]: 'Sub-Customer Admin',
      [AccountType.BranchAdmin]: 'Branch Admin',
      [AccountType.SubCompanyAdmin]: 'Sub-Company Admin',
      [AccountType.User]: 'User',
      [AccountType.LicenseeAdmin]: 'Licensee Admin',
      [AccountType.CarbonCareAdmin]: 'CarbonCare Admin',
      [AccountType.MasterAdmin]: 'Master Admin',
    };
    return labels[type] || 'Unknown';
  };

  const handleToggleUserType = (accountType: AccountType) => {
    setSelectedUserTypes(prev =>
      prev.includes(accountType)
        ? prev.filter(type => type !== accountType)
        : [...prev, accountType]
    );
  };

  // A licensee target with no user type resolves to zero recipients — flag it inline.
  const missingUserTypeTarget =
    !targetAllUsers &&
    selectedUserTypes.length === 0 &&
    (targetAllCompanies || selectedCompanies.length > 0);

  const handleCreateUpdate = async () => {
    const validationError = getAnnouncementFormError({
      title: formData.title,
      body: formData.body,
      sendViaEmail: formData.send_via_email,
      sendViaNotification: formData.send_via_notification,
      scheduleEnabled,
      scheduledDateTime,
      targetAllUsers,
      selectedUserTypeCount: selectedUserTypes.length,
      targetAllCompanies,
      selectedCompanyCount: selectedCompanies.length,
      customEmailsInput,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    // Show confirmation only when targeting all licensees
    if (targetAllCompanies && !confirmAllOpen) {
      setConfirmAllOpen(true);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const submitData: CreateAdminUpdateData = {
        title: formData.title,
        body: formData.body,
        send_via_email: formData.send_via_email,
        send_via_notification: formData.send_via_notification,
        target_user_types: targetAllUsers ? null : selectedUserTypes,
        target_company_ids: targetAllCompanies ? null : selectedCompanies.map(c => c.id),
        scheduled_at: scheduleEnabled ? new Date(scheduledDateTime).toISOString() : null,
        custom_emails: customEmailsInput.split(',').map(e => e.trim()).filter(e => e !== '') || null,
      };

      await adminUpdatesApi.create(submitData);
      setSuccess(scheduleEnabled ? 'Announcement scheduled successfully' : 'Announcement sent successfully');
      setOpenDialog(false);
      loadUpdates();
    } catch (err: any) {
      console.error('Failed to create announcement:', err);
      setError(err.response?.data?.error || 'Failed to create announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;

    try {
      setSubmitting(true);
      await adminUpdatesApi.delete(deleteId);
      setSuccess('Announcement deleted successfully');
      setDeleteConfirmOpen(false);
      setDeleteId(null);
      loadUpdates();
    } catch (err: any) {
      console.error('Failed to delete announcement:', err);
      setError(err.response?.data?.error || 'Failed to delete announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetail = async (update: AdminUpdate) => {
    setViewDetailUpdate(update);
    setViewDetailOpen(true);
    setViewDetailCompanies([]);

    if (update.target_company_ids && update.target_company_ids.length > 0) {
      try {
        setLoadingViewDetail(true);
        const response = await companyApi.getAll({
          limit: 100,
          fields: 'id,name',
        });
        const matched = response.data.filter((c: Company) =>
          update.target_company_ids!.includes(c.id)
        );
        setViewDetailCompanies(matched);
      } catch (err) {
        console.error('Failed to load company details:', err);
      } finally {
        setLoadingViewDetail(false);
      }
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { label: string; color: 'default' | 'warning' | 'success' | 'error' }> = {
      pending: { label: 'Pending', color: 'warning' },
      processing: { label: 'Processing', color: 'default' },
      sent: { label: 'Sent', color: 'success' },
      failed: { label: 'Failed', color: 'error' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    loadUpdates(newPage, rowsPerPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = Number.parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    loadUpdates(0, newSize);
  };
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(0);
  };
  const sortableHeader = (field: string, label: string) => (
    <TableSortLabel
      active={sortBy === field}
      direction={sortBy === field ? sortDir : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  // Check if user is master admin
  if (user?.accountType !== AccountType.MasterAdmin) {
    return (
      <Box>
        <Alert severity="error">
          You do not have permission to access this page. Only Master Admins can manage announcements.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        description="Publish platform-wide announcements and updates."
        actions={
          <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleOpenDialog}
              size="large"
            >
              New Announcement
            </Button>
        }
      />

      {/* Updates List */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'title' ? sortDir : false}>
                {sortableHeader('title', 'Title')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'body' ? sortDir : false}>
                {sortableHeader('body', 'Message')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'status' ? sortDir : false}>
                {sortableHeader('status', 'Status')}
              </TableCell>
              <TableCell>Delivery</TableCell>
              <TableCell sortDirection={sortBy === 'scheduled_at' ? sortDir : false}>
                {sortableHeader('scheduled_at', 'Scheduled For')}
              </TableCell>
              <TableCell sortDirection={sortBy === 'created_at' ? sortDir : false}>
                {sortableHeader('created_at', 'Created')}
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderUpdatesTableBody({
              loading,
              updates,
              getStatusChip,
              onViewDetail: handleViewDetail,
              onDeleteClick: handleDeleteClick,
            })}
          </TableBody>
        </Table>
        {renderUpdatesPagination({
          loading,
          count: totalUpdates,
          rowsPerPage,
          page,
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
        })}
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Create New Announcement</Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Title */}
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter announcement title"
              size="small"
            />

            {/* Body/Message */}
            <TextField
              fullWidth
              label="Message"
              value={formData.body}
              onChange={(e) => handleInputChange('body', e.target.value)}
              placeholder="Enter the announcement message"
              multiline
              rows={5}
              size="small"
            />

            <Divider sx={{ my: 1 }} />

            {/* Delivery & Scheduling Row */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Delivery & Scheduling
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
              <DeliveryToggleCard
                active={formData.send_via_email}
                Icon={Email}
                label="Email"
                onToggle={(next) => handleToggleChange('send_via_email', next)}
              />
              <DeliveryToggleCard
                active={formData.send_via_notification}
                Icon={Notifications}
                label="Notification"
                onToggle={(next) => handleToggleChange('send_via_notification', next)}
              />
              <DeliveryToggleCard
                active={scheduleEnabled}
                Icon={Schedule}
                label="Schedule"
                onToggle={(next) => setScheduleEnabled(next)}
              />
            </Box>

            {scheduleEnabled && (
              <CarbonCareDatePicker
                fullWidth
                readOnly
                showTime
                label="Schedule Date & Time"
                placeholder="Select date and time"
                value={scheduledDateTime || null}
                onChange={setScheduledDateTime}
                minDate={new Date()}
                filterTime={(time: Date) => time.getTime() >= Date.now()}
                portalId="root"
              />
            )}

            {!scheduleEnabled && (
              <Alert severity="info" icon={<Send fontSize="small" />} sx={{ py: 0.5 }}>
                This announcement will be sent immediately.
              </Alert>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Custom Emails */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              External Email Recipients (Comma separated)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Enter emails or paste from Excel (columns/rows)"
              value={customEmailsInput}
              onChange={(e) => setCustomEmailsInput(e.target.value)}
              onPaste={handlePasteEmails}
              size="small"
              helperText="Paste multi-line emails from Excel and they will be auto-formatted."
            />

            <Divider sx={{ my: 1 }} />

            {/* Target Audience Row */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Target Audience
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              {/* Target Users */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Users</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={targetAllUsers}
                        onChange={(e) => {
                          setTargetAllUsers(e.target.checked);
                          if (e.target.checked) setSelectedUserTypes([]);
                        }}
                      />
                    }
                    label={<Typography ml={1} variant="caption">All</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Box>
                {!targetAllUsers && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Object.values(AccountType)
                      .filter((type): type is AccountType => typeof type === 'number')
                      .map(accountType => (
                        <Chip
                          key={accountType}
                          label={getAccountTypeLabel(accountType)}
                          size="small"
                          variant={selectedUserTypes.includes(accountType) ? 'filled' : 'outlined'}
                          color={selectedUserTypes.includes(accountType) ? 'primary' : 'default'}
                          onClick={() => handleToggleUserType(accountType)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                  </Box>
                )}
                {missingUserTypeTarget && (
                  <FormHelperText error sx={{ mt: 1, ml: 0 }}>
                    Select at least one user type, or switch on All — a licensee with no user
                    type reaches nobody.
                  </FormHelperText>
                )}
              </Box>

              {/* Target Licensees */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Licensees</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={targetAllCompanies}
                        onChange={(e) => {
                          setTargetAllCompanies(e.target.checked);
                          if (e.target.checked) setSelectedCompanies([]);
                        }}
                      />
                    }
                    label={<Typography ml={1} variant="caption">All</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Box>
                {!targetAllCompanies && (
                  <Autocomplete
                    multiple
                    size="small"
                    options={companies}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    value={selectedCompanies}
                    onChange={(_event, newValue) => setSelectedCompanies(newValue)}
                    onInputChange={(_event, inputValue, reason) => {
                      if (reason === 'input') {
                        if (companySearchTimer.current) clearTimeout(companySearchTimer.current);
                        companySearchTimer.current = setTimeout(() => {
                          loadCompanies(inputValue || undefined);
                        }, 300);
                      }
                    }}
                    onOpen={() => loadCompanies()}
                    filterOptions={(x) => x}
                    loading={loadingCompanies}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Licensees"
                        placeholder="Search..."
                      />
                    )}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUpdate}
            disabled={submitting}
            startIcon={renderAnnouncementStartIcon(submitting, scheduleEnabled)}
          >
            {getAnnouncementActionVerb(submitting, scheduleEnabled)} Announcement
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Targets Dialog */}
      <Dialog open={viewDetailOpen} onClose={() => setViewDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Announcement Targets</Typography>
          <IconButton onClick={() => setViewDetailOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {viewDetailUpdate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Announcement Info */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                  Announcement
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewDetailUpdate.title}
                </Typography>
              </Box>

              <Divider />

              {/* Message */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                  Message
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {viewDetailUpdate.body}
                </Typography>
              </Box>

              <Divider />

              {/* Target Users */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People fontSize="small" />
                  Target Users
                </Typography>
                {(() => {
                  const userTypes = normalizeTargetUserTypes(viewDetailUpdate.target_user_types as any);
                  if (userTypes === null) {
                    return <Chip label="All Users" color="info" size="small" />;
                  }
                  if (userTypes.length === 0) {
                    return <Chip label="None" size="small" />;
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {userTypes.map((type: number) => (
                        <Chip
                          key={type}
                          label={getAccountTypeLabel(type)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  );
                })()}
              </Box>

              <Divider />

              {/* Target Licensees */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business fontSize="small" />
                  Target Licensees
                </Typography>
                {renderTargetLicensees({
                  targetCompanyIds: viewDetailUpdate.target_company_ids ?? null,
                  loading: loadingViewDetail,
                  companies: viewDetailCompanies,
                })}
              </Box>

              {/* Custom Emails */}
              {renderCustomEmailRecipients(viewDetailUpdate.custom_emails)}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Announcement</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this announcement? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={submitting} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Confirm All Targets Dialog */}
      <Dialog open={confirmAllOpen} onClose={() => setConfirmAllOpen(false)}>
        <DialogTitle>Confirm Announcement</DialogTitle>
        <DialogContent>
          <Typography>
            This notification will be send to all the licensees and its users.
            Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAllOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmAllOpen(false);
              handleCreateUpdate();
            }}
            color="primary"
            variant="contained"
          >
            Confirm & Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUpdatesPage;
